import { ObjectListFilters2 } from "@/components/layout-2/object-list-filters-2";
import { Filter } from "@/components/layout/types";
import { DRQPreview } from "@/components/settings/crm/drq-preview";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useObjectListFilters } from "@/hooks/use-object-list-filters";
import { cn } from "@/lib/utils";
import { useUser } from "@/providers/user";
import { CRMSharingRulesService } from "@/services/crm-sharing-rules";
import { DRQConditionUI } from "@/types/crm/drq-builder";
import { CRMObject } from "@/types/crm/permissions";
import { SharingRule, SharingRuleIndexStatus, SharingRuleMatchedUser } from "@/types/crm/sharing-rules";
import { AnyDRQQuery, drqToEnglish } from "@/utils/drq";
import * as LucideIcons from "lucide-react";
import { Edit, Plus, Power, PowerOff, RefreshCw, Trash2, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const INDEX_STATUS_CONFIG: Record<SharingRuleIndexStatus, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; className: string }> = {
    pending: { label: "Pending", variant: "outline", className: "border-amber-400 text-amber-600" },
    indexing: { label: "Indexing", variant: "outline", className: "border-blue-400 text-blue-600 animate-pulse" },
    indexed: { label: "Indexed", variant: "outline", className: "border-green-500 text-green-700" },
    error: { label: "Error", variant: "destructive", className: "" }
};

function IndexStatusBadge({ rule }: { rule: SharingRule }) {
    const cfg = INDEX_STATUS_CONFIG[rule.index_status] ?? INDEX_STATUS_CONFIG.pending;
    const badge = (
        <div className="flex items-center gap-1.5">
            <Badge variant={cfg.variant} className={cn("cursor-default text-xs", cfg.className)}>
                {cfg.label}
            </Badge>
            {rule.index_status === "indexed" && rule.matched_users_count != null && (
                <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-xs font-medium tabular-nums text-green-700">{rule.matched_users_count}</span>
            )}
        </div>
    );

    if (rule.index_status === "error" && rule.index_error) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>{badge}</TooltipTrigger>
                    <TooltipContent className="max-w-xs break-words text-xs">{rule.index_error}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }
    if (rule.index_status === "indexed" && rule.index_updated_at) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>{badge}</TooltipTrigger>
                    <TooltipContent className="text-xs">Last indexed: {new Date(rule.index_updated_at).toLocaleString()}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }
    return badge;
}

// Champs disponibles pour les utilisateurs (exemple)
const USER_FIELDS: Filter[] = [
    { field_name: "email", name: "Email", type: "text" },
    { field_name: "profile", name: "Profile", type: "select", optionsUrl: "/crm/objects/users/picklists/profile/" },
    { field_name: "sf_user__user_role__name", name: "Role", type: "text" },
    {
        field_name: "sf_user__sales_process",
        name: "Sales Process",
        type: "select",
        optionsUrl: "/crm/objects/users/picklists/sales_process/"
    },
    {
        field_name: "sf_user__site",
        name: "Site",
        type: "select",
        optionsUrl: "/crm/objects/users/picklists/site/"
    }
];

// Fonction pour générer les champs disponibles pour un objet à partir de ses données
const generateObjectFields = (objectData: CRMObject | undefined): Filter[] => {
    if (!objectData || !objectData.fields) {
        return [];
    }

    return objectData.fields
        .sort((a, b) => (a.split(".").length - b.split(".").length) * objectData.fields.length + a.localeCompare(b))
        .map(fieldName => {
            // Déterminer le type de champ basé sur le nom
            let type: Filter["type"] = "text";
            if (fieldName.includes("date") || fieldName.includes("datetime") || fieldName.includes("created_at") || fieldName.includes("updated_at") || fieldName.endsWith("_at")) {
                type = "datetime-range";
            } else if (fieldName.includes("id")) {
                type = "text";
            } else if (fieldName.includes("is_") || fieldName.includes("has_")) {
                type = "checkbox";
            } else if (fieldName.includes(".")) {
                // Champs de relation
                type = "text";
            }

            return {
                field_name: fieldName.replace(".", "__"),
                name: fieldName,
                type: type
            };
        });
};

// Fonction pour convertir FilterInstance en DRQConditionUI pour la compatibilité
const convertFilterInstanceToDRQConditionUI = (filterInstance: any): DRQConditionUI => ({
    id: filterInstance.id,
    field_name: filterInstance.field_name,
    operator: filterInstance.operator,
    value: filterInstance.value,
    type: filterInstance.type as any
});

interface SharingRulesTableProps {
    rules: SharingRule[];
    objectFields: Filter[];
    onOpenMatchedUsers: (rule: SharingRule) => void;
    onReindex: (rule: SharingRule) => void;
    onToggleActive: (rule: SharingRule) => void;
    onEditRule: (rule: SharingRule) => void;
    onDeleteRule: (rule: SharingRule) => void;
    emptyMessage?: string;
}

function SharingRulesTable({ rules, objectFields, onOpenMatchedUsers, onReindex, onToggleActive, onEditRule, onDeleteRule, emptyMessage = "No rule defined" }: SharingRulesTableProps) {
    return (
        <div className="overflow-x-auto">
            <table className="w-full border-collapse">
                <thead className="rounded-md bg-gray-100">
                    <tr>
                        <th className="rounded-l px-4 py-2 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Index</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Conditions on the user</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Conditions on the object</th>
                        <th className="px-4 py-2 text-left text-sm font-medium">Error</th>
                        <th className="rounded-r px-4 py-2 text-center text-sm font-medium">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {rules.map(rule => (
                        <tr key={rule.id} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-2">
                                <TooltipProvider>
                                    <div className="flex items-center gap-1">
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span
                                                    className={cn(
                                                        "inline-flex h-6 w-6 cursor-default items-center justify-center rounded-full",
                                                        rule.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                                                    )}
                                                >
                                                    {rule.active ? <Power className="h-3.5 w-3.5" /> : <PowerOff className="h-3.5 w-3.5" />}
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent className="text-xs">{rule.active ? "Active" : "Inactive"}</TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span
                                                    className={cn(
                                                        "inline-flex h-6 w-6 cursor-default items-center justify-center rounded-full",
                                                        rule.use_hierarchy ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-300"
                                                    )}
                                                >
                                                    <LucideIcons.GitBranch className="h-3.5 w-3.5" />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent className="text-xs">
                                                {rule.use_hierarchy ? "Hierarchy enabled — managers of matched users are also granted access" : "Hierarchy disabled"}
                                            </TooltipContent>
                                        </Tooltip>
                                    </div>
                                </TooltipProvider>
                            </td>
                            <td className="px-4 py-2">
                                <IndexStatusBadge rule={rule} />
                            </td>
                            <td className="px-4 py-2">
                                {rule.user_drq && Object.keys(rule.user_drq).length > 0 ? (
                                    <div className="break-words text-sm">
                                        {drqToEnglish(rule.user_drq as AnyDRQQuery, {
                                            fieldLabels: Object.fromEntries(USER_FIELDS.map(f => [f.field_name, f.name ?? f.field_name])) as Record<string, string>
                                        }) || "Defined"}
                                    </div>
                                ) : (
                                    <div className="text-sm text-muted-foreground">All users</div>
                                )}
                            </td>
                            <td className="px-4 py-2">
                                {rule.object_drq && Object.keys(rule.object_drq).length > 0 ? (
                                    <div className="break-words text-sm">
                                        {drqToEnglish(rule.object_drq as AnyDRQQuery, {
                                            fieldLabels: Object.fromEntries(objectFields.map(f => [f.field_name, f.name ?? f.field_name])) as Record<string, string>
                                        }) || "Defined"}
                                    </div>
                                ) : (
                                    <div className="text-sm text-muted-foreground">All records</div>
                                )}
                            </td>
                            <td className="px-4 py-2">{rule.error ? <div className="text-sm text-destructive">{rule.error}</div> : <div className="text-sm text-muted-foreground">—</div>}</td>
                            <td className="px-4 py-2">
                                <div className="flex items-center justify-center gap-1">
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button variant="ghost" size="sm" onClick={() => onOpenMatchedUsers(rule)}>
                                                    <Users className="h-4 w-4" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent className="text-xs">View matched users</TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <Button variant="ghost" size="sm" onClick={() => onReindex(rule)}>
                                                    <RefreshCw className="h-4 w-4" />
                                                </Button>
                                            </TooltipTrigger>
                                            <TooltipContent className="text-xs">Re-index</TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                    <Button variant="ghost" size="sm" onClick={() => onToggleActive(rule)}>
                                        {rule.active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => onEditRule(rule)}>
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => onDeleteRule(rule)} className="text-destructive hover:text-destructive">
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    {rules.length === 0 && (
                        <tr>
                            <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">
                                {emptyMessage}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export function CrmSharingRulesPage() {
    const { has_any_profile } = useUser();
    const [searchParams, setSearchParams] = useSearchParams();
    const [objects, setObjects] = useState<CRMObject[]>([]);
    const [sharingRules, setSharingRules] = useState<SharingRule[]>([]);

    const selectedObject = searchParams.get("object") ?? "";
    const ruleParam = searchParams.get("rule"); // "new" | "<id>" | null
    const showForm = ruleParam !== null;

    const setSelectedObject = useCallback(
        (value: string) => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev);
                    if (value) {
                        next.set("object", value);
                    } else {
                        next.delete("object");
                    }
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    const openForm = useCallback(
        (ruleId: number | "new") => {
            setSearchParams(
                prev => {
                    const next = new URLSearchParams(prev);
                    next.set("rule", String(ruleId));
                    return next;
                },
                { replace: false }
            );
        },
        [setSearchParams]
    );

    const closeForm = useCallback(() => {
        setSearchParams(
            prev => {
                const next = new URLSearchParams(prev);
                next.delete("rule");
                return next;
            },
            { replace: true }
        );
    }, [setSearchParams]);

    const [loading, setLoading] = useState(false);
    const [editingRule, setEditingRule] = useState<SharingRule | null>(null);
    const initializedRuleParam = useRef<string | null>(null);

    // États pour le constructeur DRQ - Utilisation du hook unifié
    const {
        drqFilterState: userFilterState,
        filterLogic: userFilterLogic,
        currentDRQQuery: userDRQQuery,
        handleAddFilter: handleAddUserFilter,
        handleUpdateFilter: handleUpdateUserFilter,
        handleRemoveFilter: handleRemoveUserFilter,
        handleClearAllFilters: handleClearUserFilters,
        handleSetFilterLogic: setUserFilterLogic,
        loadFiltersFromDRQ: loadUserFiltersFromDRQ
    } = useObjectListFilters();

    const {
        drqFilterState: objectFilterState,
        filterLogic: objectFilterLogic,
        currentDRQQuery: objectDRQQuery,
        handleAddFilter: handleAddObjectFilter,
        handleUpdateFilter: handleUpdateObjectFilter,
        handleRemoveFilter: handleRemoveObjectFilter,
        handleClearAllFilters: handleClearObjectFilters,
        handleSetFilterLogic: setObjectFilterLogic,
        loadFiltersFromDRQ: loadObjectFiltersFromDRQ
    } = useObjectListFilters();

    const [isActive, setIsActive] = useState(true);
    const [useHierarchy, setUseHierarchy] = useState(false);

    // Modale utilisateurs indexés
    const [matchedUsersRule, setMatchedUsersRule] = useState<SharingRule | null>(null);
    const [matchedUsers, setMatchedUsers] = useState<SharingRuleMatchedUser[]>([]);
    const [matchedUsersLoading, setMatchedUsersLoading] = useState(false);
    const [matchedUsersSearch, setMatchedUsersSearch] = useState("");

    const loadObjects = useCallback(async () => {
        try {
            const objectsData = await CRMSharingRulesService.getObjects();
            setObjects(objectsData);
        } catch (error) {
            toast.error("Erreur lors du chargement des objets");
        }
    }, []);

    const loadSharingRules = useCallback(async () => {
        setLoading(true);
        try {
            const objectId = parseInt(selectedObject);
            if (!selectedObject || isNaN(objectId)) return;
            const rulesData = await CRMSharingRulesService.getSharingRules(objectId);
            setSharingRules(rulesData);
        } catch (error) {
            toast.error("Erreur lors du chargement des règles");
        } finally {
            setLoading(false);
        }
    }, [selectedObject]);

    useEffect(() => {
        loadObjects();
    }, [loadObjects]);

    useEffect(() => {
        loadSharingRules();
    }, [loadSharingRules]);

    // Initialise le formulaire depuis l'URL quand les règles sont chargées
    useEffect(() => {
        if (!ruleParam || ruleParam === "new") {
            if (ruleParam !== initializedRuleParam.current) {
                initializedRuleParam.current = ruleParam;
                if (ruleParam === "new") {
                    setEditingRule(null);
                    handleClearUserFilters();
                    handleClearObjectFilters();
                    setIsActive(true);
                    setUseHierarchy(false);
                }
            }
            return;
        }
        if (ruleParam === initializedRuleParam.current) return;
        if (sharingRules.length === 0) return;

        const ruleId = parseInt(ruleParam);
        const rule = sharingRules.find(r => r.id === ruleId && String(r.reference_type_id) === selectedObject);
        if (!rule) return;

        initializedRuleParam.current = ruleParam;
        setEditingRule(rule);
        try {
            loadUserFiltersFromDRQ(rule.user_drq, true);
            loadObjectFiltersFromDRQ(rule.object_drq, true);
            setIsActive(rule.active);
            setUseHierarchy(rule.use_hierarchy ?? false);
        } catch {
            toast.error("Erreur lors du chargement des conditions");
        }
    }, [ruleParam, sharingRules]);

    // Réinitialise la ref quand on ferme le formulaire
    useEffect(() => {
        if (!ruleParam) {
            initializedRuleParam.current = null;
        }
    }, [ruleParam]);

    const selectedObjectData = objects.find(obj => obj.reference_type_id === selectedObject);
    const objectFields = useMemo(() => generateObjectFields(selectedObjectData), [selectedObjectData]);

    // Vérifier les permissions après les hooks
    if (!has_any_profile(["system_admin"])) {
        return (
            <div className="flex h-64 items-center justify-center">
                <p className="text-muted-foreground">Accès non autorisé</p>
            </div>
        );
    }

    const handleCreateRule = () => {
        openForm("new");
    };

    const handleEditRule = (rule: SharingRule) => {
        setEditingRule(rule);
        try {
            loadUserFiltersFromDRQ(rule.user_drq, true);
            loadObjectFiltersFromDRQ(rule.object_drq, true);
            setIsActive(rule.active);
            setUseHierarchy(rule.use_hierarchy ?? false);
        } catch (error) {
            toast.error("Erreur lors du chargement des conditions");
        }
        initializedRuleParam.current = String(rule.id);
        openForm(rule.id);
    };

    const handleSaveRule = async () => {
        const objectId = parseInt(selectedObject);
        if (!selectedObject || isNaN(objectId)) {
            toast.error("Veuillez sélectionner un objet");
            return;
        }

        const isEditMode = ruleParam !== null && ruleParam !== "new";
        if (isEditMode && !editingRule) {
            toast.error("La règle à modifier est introuvable, veuillez recharger la page.");
            return;
        }

        try {
            const ruleData = {
                reference_type_id: objectId,
                user_drq: userDRQQuery as AnyDRQQuery,
                object_drq: objectDRQQuery as AnyDRQQuery,
                active: isActive,
                use_hierarchy: useHierarchy
            };

            if (editingRule) {
                await CRMSharingRulesService.updateSharingRule(editingRule.id, ruleData);
                toast.success("Règle mise à jour");
            } else {
                await CRMSharingRulesService.createSharingRule(ruleData);
                toast.success("Règle créée");
            }

            setEditingRule(null);
            closeForm();
            loadSharingRules();
        } catch (error) {
            toast.error("Erreur lors de la sauvegarde");
        }
    };

    const handleDeleteRule = async (rule: SharingRule) => {
        if (!confirm("Êtes-vous sûr de vouloir supprimer cette règle ?")) {
            return;
        }

        try {
            await CRMSharingRulesService.deleteSharingRule(rule.id);
            toast.success("Règle supprimée");
            loadSharingRules();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    const handleOpenMatchedUsers = async (rule: SharingRule) => {
        setMatchedUsersRule(rule);
        setMatchedUsers([]);
        setMatchedUsersSearch("");
        if (rule.index_status !== "indexed") return;
        setMatchedUsersLoading(true);
        try {
            const resp = await CRMSharingRulesService.getMatchedUsers(rule.id);
            setMatchedUsers(resp.results);
        } catch {
            toast.error("Erreur lors du chargement des utilisateurs");
        } finally {
            setMatchedUsersLoading(false);
        }
    };

    const handleReindex = async (rule: SharingRule) => {
        try {
            await CRMSharingRulesService.reindex(rule.id);
            toast.success("Ré-indexation planifiée");
            loadSharingRules();
        } catch {
            toast.error("Erreur lors de la planification de la ré-indexation");
        }
    };

    const handleToggleActive = async (rule: SharingRule) => {
        try {
            await CRMSharingRulesService.toggleSharingRule(rule.id);
            toast.success("Statut modifié");
            loadSharingRules();
        } catch (error) {
            toast.error("Erreur lors de la modification du statut");
        }
    };

    const IconComponent = selectedObjectData?.icon && (LucideIcons as any)[selectedObjectData.icon];
    const IconColor = selectedObjectData?.color || "bg-gray-500";

    // Convertir les FilterInstance en DRQConditionUI pour la compatibilité avec DRQPreview
    const userConditionsForPreview = userFilterState.map(convertFilterInstanceToDRQConditionUI);
    const objectConditionsForPreview = objectFilterState.map(convertFilterInstanceToDRQConditionUI);

    if (showForm) {
        return (
            <div className="flex flex-col rounded-md bg-white p-6 shadow-md">
                <div className="mb-4 flex items-center justify-end">
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={closeForm}>
                            Cancel
                        </Button>
                        <Button onClick={handleSaveRule}>Save</Button>
                    </div>
                </div>

                <div>
                    <h1 className="text-md font-bold">Conditions on the user</h1>
                </div>
                <div className="mt-4 grid grid-cols-12 items-start gap-6 rounded-md border bg-gray-100 p-4  pt-0">
                    {/* Conditions utilisateur */}

                    <div className="col-span-6">
                        <ObjectListFilters2
                            filters={USER_FIELDS}
                            filterState={userFilterState}
                            onAddFilter={handleAddUserFilter}
                            onUpdateFilter={handleUpdateUserFilter}
                            onRemoveFilter={handleRemoveUserFilter}
                            onClearAllFilters={handleClearUserFilters}
                            currentLogic={userFilterLogic}
                            onSetLogic={setUserFilterLogic}
                            // title=""
                            searchButtonText="Validate conditions"
                        />
                    </div>

                    <div className="mx-auto flex w-fit items-center justify-center self-center rounded-full border bg-white p-2">
                        <LucideIcons.ArrowRight className="h-5 w-5" />
                    </div>

                    <div className="col-span-5">
                        <div className="text-md mb-6 mt-4 font-bold">Query Preview</div>
                        <DRQPreview
                            conditions={userConditionsForPreview}
                            drqQuery={userDRQQuery}
                            referenceTypeId={selectedObject ? parseInt(selectedObject) : undefined}
                            queryType="user"
                            title="Preview - Conditions on the user"
                        />
                    </div>
                </div>

                {/* Hierarchy toggle */}
                <div className="mt-6 flex items-start gap-4 rounded-md border bg-blue-50 p-4">
                    <div className="flex items-center gap-3 pt-0.5">
                        <Switch id="use-hierarchy" checked={useHierarchy} onCheckedChange={setUseHierarchy} />
                    </div>
                    <div>
                        <Label htmlFor="use-hierarchy" className="cursor-pointer font-semibold">
                            Propagate to managers (hierarchy)
                        </Label>
                        <p className="mt-0.5 text-sm text-muted-foreground">
                            When enabled, every manager of a matched user (transitively) will also be granted access through this rule. Uses the{" "}
                            <span className="font-mono text-xs">User.managers</span> relationship.
                        </p>
                    </div>
                </div>

                <div>
                    <h1 className="text-md mt-10 font-bold">Conditions on the records</h1>
                </div>
                <div className="mt-4 grid grid-cols-12 items-start gap-6 rounded-md border bg-off-white p-4 pb-6 pt-0">
                    {/* Conditions objet */}

                    <div className="col-span-6">
                        <ObjectListFilters2
                            filters={objectFields}
                            filterState={objectFilterState}
                            onAddFilter={handleAddObjectFilter}
                            onUpdateFilter={handleUpdateObjectFilter}
                            onRemoveFilter={handleRemoveObjectFilter}
                            onClearAllFilters={handleClearObjectFilters}
                            currentLogic={objectFilterLogic}
                            onSetLogic={setObjectFilterLogic}
                            searchButtonText="Validate conditions"
                        />
                    </div>
                    <div className="mx-auto flex w-fit items-center justify-center self-center rounded-full border bg-white p-2">
                        <LucideIcons.ArrowRight className="h-5 w-5" />
                    </div>

                    <div className="col-span-5">
                        <div className="text-md mb-6 mt-4 font-bold">Query Preview</div>
                        <DRQPreview
                            conditions={objectConditionsForPreview}
                            drqQuery={objectDRQQuery}
                            referenceTypeId={selectedObject ? parseInt(selectedObject) : undefined}
                            queryType="object"
                            title="Preview - Conditions on the object"
                        />
                    </div>
                </div>

                {/* Prévisualisation */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2"></div>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col gap-4 rounded-md bg-white p-6 shadow-md">
                <div>
                    <h1 className="text-md font-bold">Sharing rules</h1>
                    <p className="text-sm text-muted-foreground">Configure the sharing rules to extend the access permissions to CRM objects.</p>
                </div>

                <div className="flex flex-row items-center gap-4">
                    <div className="flex-1">
                        <Combobox
                            name="selectedObject"
                            value={selectedObject}
                            options={objects.map(obj => ({ value: obj.reference_type_id, label: obj.label }))}
                            placeholder="Select a CRM object"
                            commandPlaceholder="Search an object..."
                            onValueChange={setSelectedObject}
                            className="w-full"
                        />
                    </div>
                    <Button onClick={handleCreateRule} disabled={!selectedObject}>
                        <Plus className="mr-2 h-4 w-4" />
                        Create a rule
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="flex h-32 items-center justify-center rounded-md bg-white shadow-md">
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            ) : selectedObject ? (
                <div className="flex flex-col gap-4 rounded-md bg-white p-6 shadow-md">
                    <div className="mb-4 flex flex-row items-center gap-2">
                        <div className={cn("flex h-7 w-7 flex-shrink-0 items-center justify-between rounded")} style={{ backgroundColor: IconColor }}>
                            {IconComponent && <IconComponent className="mx-auto flex h-5 w-5 text-center text-white" strokeWidth={1.5} />}
                        </div>
                        <div className="text-md w-full truncate pr-4 font-bold text-gray-800">{selectedObjectData?.label} sharing rules</div>
                    </div>

                    <SharingRulesTable
                        rules={sharingRules}
                        objectFields={objectFields}
                        onOpenMatchedUsers={handleOpenMatchedUsers}
                        onReindex={handleReindex}
                        onToggleActive={handleToggleActive}
                        onEditRule={handleEditRule}
                        onDeleteRule={handleDeleteRule}
                        emptyMessage="No rule defined for this object"
                    />
                </div>
            ) : (
                <div className="flex h-32 items-center justify-center rounded-md bg-white shadow-md">
                    <p className="text-muted-foreground">Select a CRM object to view its sharing rules.</p>
                </div>
            )}

            {/* Modale utilisateurs indexés */}
            <Dialog
                open={matchedUsersRule !== null}
                onOpenChange={open => {
                    if (!open) setMatchedUsersRule(null);
                }}
            >
                <DialogContent className="max-w-4xl">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            Matched users
                            {matchedUsersRule && <span className="ml-1 text-sm font-normal text-muted-foreground">— Rule #{matchedUsersRule.id}</span>}
                        </DialogTitle>
                    </DialogHeader>

                    {matchedUsersRule && matchedUsersRule.index_status !== "indexed" ? (
                        <div className="py-6 text-center text-sm text-muted-foreground">
                            <p>The index is not ready yet.</p>
                            <p className="mt-1">
                                Current status: <IndexStatusBadge rule={matchedUsersRule} />
                            </p>
                            <Button variant="outline" size="sm" className="mt-4" onClick={() => handleReindex(matchedUsersRule)}>
                                <RefreshCw className="mr-2 h-4 w-4" />
                                Trigger re-index
                            </Button>
                        </div>
                    ) : matchedUsersLoading ? (
                        <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">Loading…</div>
                    ) : (
                        (() => {
                            const q = matchedUsersSearch.trim().toLowerCase();
                            const filtered = q
                                ? matchedUsers.filter(
                                      u => u.full_name?.toLowerCase().includes(q) || u.username.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.profile.toLowerCase().includes(q)
                                  )
                                : matchedUsers;
                            return (
                                <div className="flex flex-col gap-3">
                                    <Input
                                        placeholder="Search by name, email, username or profile…"
                                        value={matchedUsersSearch}
                                        onChange={e => setMatchedUsersSearch(e.target.value)}
                                        className="h-8 text-sm"
                                    />
                                    <div className="max-h-[55vh] overflow-y-auto">
                                        {filtered.length === 0 ? (
                                            <p className="py-6 text-center text-sm text-muted-foreground">{matchedUsers.length === 0 ? "No matched users." : "No results for this search."}</p>
                                        ) : (
                                            <table className="w-full border-collapse text-sm">
                                                <thead className="sticky top-0 bg-gray-100">
                                                    <tr>
                                                        <th className="rounded-l px-3 py-2 text-left font-medium">Name</th>
                                                        <th className="px-3 py-2 text-left font-medium">Username</th>
                                                        <th className="px-3 py-2 text-left font-medium">Email</th>
                                                        <th className="rounded-r px-3 py-2 text-left font-medium">Profile</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filtered.map(user => (
                                                        <tr key={user.id} className="border-b hover:bg-gray-50">
                                                            <td className="px-3 py-2 font-medium">{user.full_name || "—"}</td>
                                                            <td className="px-3 py-2 text-muted-foreground">{user.username}</td>
                                                            <td className="px-3 py-2 text-muted-foreground">{user.email}</td>
                                                            <td className="px-3 py-2">
                                                                <Badge variant="outline" className="text-xs">
                                                                    {user.profile}
                                                                </Badge>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                        <p className="mt-2 text-right text-xs text-muted-foreground">
                                            {q ? `${filtered.length} / ${matchedUsers.length}` : matchedUsers.length} user{matchedUsers.length !== 1 ? "s" : ""}
                                        </p>
                                    </div>
                                </div>
                            );
                        })()
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}
